<template>
    <q-dialog
        full-width
        full-height
        ref="workOrderDialogRef"
        @hide="onDialogHide"
        v-model="workOrderDialogStore.open"
        :maximized="$q.screen.lt.md"
        :no-focus="(minimized || docked)"
        :seamless="(minimized || docked)"
        :position="(minimized || docked) ? 'bottom' : undefined"
    >
        <q-card
            :flat="$q.dark.isActive"
            :square="$q.dark.isActive"
            :bordered="$q.dark.isActive"
            class="q-dialog-plugin relative-position"
        >
            <q-toolbar
                :class="{
                    'bg-primary text-white': !$q.dark.isActive,
                    'cursor-pointer': minimized,
                }"
                @click="minimized ? (minimized = false) : undefined"
                :style="($q.dark.isActive && (minimized || docked)) ? `background-color: #0f0f0f !important;` : ''"
            >
                <q-toolbar-title :class="{'text-subtitle1': $q.screen.lt.md}">
                    <span v-if="workOrderId < 1">Creating A New Work Order For </span>
                    <span 
                        v-if="workOrder.contact?.org_name ?? false" 
                        :class="{'text-secondary':$q.dark.isActive}"
                    >
                        {{ workOrder.contact.org_name ?? '*loading...*' }}
                    </span>&nbsp;<span v-if="workOrderId > 0">Work Order</span>
                    <span :class="{'text-secondary':$q.dark.isActive}" v-if="workOrderId > 0">
                        {{ workOrderId ? ' #' + workOrderId : '' }}
                    </span>
                </q-toolbar-title>

                <q-btn
                    flat
                    padding="3px"
                    :icon="'mdi-' + (!minimized ? 'window-minimize' : 'chevron-up')"
                    @click.stop="minimized = !minimized"
                >
                    <q-tooltip
                        anchor="top middle"
                        self="bottom middle"
                        :offset="[0, 10]"
                    >
                        {{ minimized ? 'Restore' : 'Minimize' }}
                    </q-tooltip>
                </q-btn>

                <q-btn
                    flat
                    padding="3px"
                    :icon="'mdi-window-' + (docked ? 'maximize' : 'restore')"
                    @click.stop="docked = !docked"
                    v-if="$q.screen.gt.sm"
                />

                <q-btn
                    flat
                    padding="3px"
                    icon="mdi-close"
                    @click.stop="workOrderDialogStore.closeDialog()"
                />
            </q-toolbar>

            <q-separator v-if="!minimized" />

            <q-card-section v-if="!minimized" style="height: calc(100% - 97px); position: relative;">
                <div class="row full-height" :style="$q.screen.lt.md ? 'flex-direction: column;' : ''">
                    <div class="col-12 col-md-6 col-lg-4" :style="$q.screen.lt.md ? 'flex: 0;' : ''">
                        <WorkOrderDetails
                            v-model:work-order="workOrderDialogStore.workOrder"
                            v-model:is-locked="workOrderDialogStore.locked"
                            :editable="workOrderDialogStore.workOrder.status !== 'invoiced'"
                            :editing="workOrderDialogStore.editing"
                            :is-modified="workOrderDialogStore.isDirty"
                            :is-new="workOrderDialogStore.workOrder.id === null"
                            :new-work-items-length="newWorkItems.length"
                            :work-items-length="workOrderDialogStore.workItems.rows.length"
                            :loading="workOrderDialogStore.loading.workOrder"
                            @edit="workOrderDialogStore.editing = true"
                            @cancel="workOrderDialogStore.editing = false"
                            @save="workOrderDialogStore.submitWorkOrder()"
                            @delete="workOrderDialogStore.deleteWorkOrder()"
                            @update:date="v => workOrderDialogStore.updateWorkOrderDate(v)"
                            @update:contact="c => workOrderDialogStore.updateWorkOrderContact(c)"
                        />
                        <!-- Search/filter for work items -->
                        <q-card 
                            flat 
                            square 
                            bordered
                            class="q-mt-sm"
                            :style="$q.dark.isActive ? `background-color: #0f0f0f !important;` : ''"
                        >
                            <q-card-section class="q-pa-none">
                                <q-expansion-item
                                    expand-icon-toggle
                                    expand-icon="mdi-filter-menu"
                                    expanded-icon="mdi-filter-minus"
                                    :header-style="$q.dark.isActive ? `background-color: transparent !important;` : ''"
                                    v-if="workOrder.id"
                                >
                                    <template #header>
                                        <q-item-section>
                                            <q-input
                                                v-model="workItemFilter.search"
                                                square
                                                outlined
                                                dense
                                                debounce="300"
                                                clearable
                                                placeholder="Search..."
                                                :label="`Search ${workItemFilter.searchColumn.length > 0 ? 'selected' : 'all'} column${workItemFilter.searchColumn.length != 1 ? 's' : ''}`"
                                            >
                                                <template #prepend>
                                                    <q-icon name="mdi-magnify" />
                                                </template>
                                                <template #append v-if="loading.workItems">
                                                    <q-spinner size="1.2rem" />
                                                </template>
                                            </q-input>
                                        </q-item-section>
                                        <q-item-section style="padding-left: 5px;" side>
                                            <q-btn
                                                flat
                                                @click="workOrderDialogStore.fetch(null, null, true)"
                                                :disable="loading.workItems"
                                                :color="loading.workItems ? 'secondary' : undefined"
                                                padding="8.5px"
                                            >
                                                <q-icon name="mdi-refresh" :class="{spin: loading.workItems}" />
                                            </q-btn>
                                        </q-item-section> 
                                        <q-item-section v-if="$q.screen.lt.md" style="padding-left: 5px;" side>
                                            <q-btn
                                                flat
                                                :disable="loading.workItems"
                                                :color="$q.dark.isActive ? 'green-13' : 'green'"
                                                icon="mdi-playlist-plus"
                                                padding="8.5px"
                                                @click="workOrderDialogStore.create()"
                                            />
                                        </q-item-section>
                                    </template>

                                    <div :class="{'q-px-md row': 1, 'q-pb-sm': $q.screen.lt.md}">
                                        <div class="col-12 col-md-6 col-lg-8">
                                            <q-select
                                                v-model="workItemFilter.searchColumn"
                                                square
                                                use-chips
                                                stack-label
                                                :class="{
                                                    'q-mr-sm': $q.screen.gt.sm,
                                                    'q-mb-sm': $q.screen.lt.md
                                                }"
                                                outlined
                                                dense
                                                multiple
                                                emit-value
                                                map-options
                                                :options="workItemColumns"
                                                placeholder="Search Columns..."
                                                label="Search Columns"
                                                :disable="loading.workItems"
                                            />
                                        </div>

                                        <div class="col-12 col-md-6 col-lg-4">
                                            <q-select
                                                square
                                                outlined
                                                dense
                                                emit-value
                                                map-options
                                                :options="[{label: 'Created At',value: 'created_at'},{label: 'Updated At',value: 'updated_at'}]"
                                                placeholder="Sort By..."
                                                label="Sort By Column"
                                                v-model="workItemFilter.sort"
                                                :disable="loading.workItems"
                                            >
                                                <template #after>
                                                    <q-btn
                                                        flat
                                                        padding="8.5px"
                                                        :icon="'mdi-sort-' + (workItemFilter.direction === 'asc' ? 'ascending' : 'descending')"
                                                        @click.prevent="workItemFilter.direction = workItemFilter.direction === 'asc' ? 'desc' : 'asc'"
                                                    >
                                                        <q-tooltip>
                                                            {{ workItemFilter.direction === 'asc' ? 'Descending' : 'Ascending' }}
                                                        </q-tooltip>
                                                    </q-btn>
                                                </template>
                                            </q-select>
                                        </div>

                                        <div class="col-12 q-mt-sm">
                                            <q-select
                                                dense
                                                square
                                                outlined
                                                emit-value
                                                map-options
                                                v-model="workItemFilter.rows"
                                                :options="rows"
                                                label="Rows Per Page"
                                                :disable="loading.workItems"
                                            />
                                        </div>
                                    </div>
                                </q-expansion-item>

                                <!-- {{ JSON.stringify(workOrderDialogStore) }} -->
                            </q-card-section>

                            <q-item v-if="$q.screen.gt.sm" :class="{'q-py-none': workOrder.id}">
                                <q-item-section>
                                    <q-item-label v-if="workItemFilter.total > 0" caption>
                                        <!-- {{ workItemFilterOptions }} -->
                                        <!-- Showing n of n work items -->
                                    </q-item-label>
                                </q-item-section>
                                <q-item-section side>
                                    <q-btn
                                        v-if="((workOrderDialogStore.editing || workOrderDialogStore.locked) || workOrder.status !== 'invoiced')"
                                        icon="mdi-playlist-plus"
                                        :color="$q.dark.isActive ? 'green-13' : 'green'"
                                        label="Add New Work Item"
                                        size="sm"
                                        @click="workOrderDialogStore.create()"
                                        :outline="$q.dark.isActive"
                                    >
                                        <q-tooltip>
                                            Add New Work Item
                                        </q-tooltip>
                                    </q-btn>
                                </q-item-section>
                            </q-item>
                        </q-card>
                    </div>
                    
                    <div :style="$q.screen.lt.md ? 'flex: 1;' : ''" :class="{
                        'col-12 col-md-6 col-lg-8': 1,
                    }">
                        <q-scroll-area
                            :visible="true"
                            style="height: calc(100%);"
                            :class="{
                                'q-pt-sm': $q.screen.lt.md,
                                'q-px-md': $q.screen.gt.sm,
                            }"
                        >
                            <q-card
                                flat
                                square
                                bordered
                                v-for="entry in newWorkItems"
                                :key="`n-${entry.index}`"
                                class="q-mb-sm"
                            >
                                <WorkItemEdit
                                    v-model="workOrderDialogStore.workItems.editing[entry.index]"
                                    :customer="customer"
                                    :disable-create="!workOrder.id"
                                    :identifier-types="identifierTypes"
                                    :work-start-at="workOrder.work_start_at"
                                    :error="newItemError(entry.index)"
                                    @save="workOrderDialogStore.saveWorkItem(entry.index)"
                                    @cancel="workOrderDialogStore.cancelEdit(entry.index)"
                                    @save-all="workOrderDialogStore.submitWorkOrder()"
                                    @delete="workOrderDialogStore.destroyWorkItem(entry.item, entry.index)"
                                />
                            </q-card>

                            <q-card
                                flat
                                square
                                bordered
                                v-for="(workItem, index) in workItems"
                                :key="`r-${index}-${workItem.id}`"
                                class="q-mb-sm"
                            >
                                <WorkItemEdit
                                    v-if="editingIndexFor(workItem.id) !== -1"
                                    v-model="workOrderDialogStore.workItems.editing[editingIndexFor(workItem.id)]"
                                    :customer="customer"
                                    :disable-create="!workOrder.id"
                                    :identifier-types="identifierTypes"
                                    :work-start-at="workOrder.work_start_at"
                                    :error="existingItemError(workItem.id)"
                                    @save="workOrderDialogStore.saveWorkItem(editingIndexFor(workItem.id))"
                                    @cancel="workOrderDialogStore.cancelEdit(editingIndexFor(workItem.id))"
                                    @save-all="workOrderDialogStore.submitWorkOrder()"
                                    @delete="workOrderDialogStore.destroyWorkItem(workItem, editingIndexFor(workItem.id))"
                                />

                                <WorkItemView
                                    v-else
                                    :customer="customer"
                                    :model-value="workItem"
                                    :enable-crud="!workOrderDialogStore.locked"
                                    :locked="workOrderDialogStore.locked"
                                    :work-start-at="workOrder.work_start_at"
                                    @edit="workOrderDialogStore.beginEdit(workItem)"
                                    @delete="workOrderDialogStore.destroyWorkItem(workItem)"
                                />
                            </q-card>

                            <q-inner-loading :showing="!loading.workItems && workItems.length === 0 && workItemsEditing.length === 0">
                                <q-item-label class="text-h6 text-grey-5" v-if="!loading.workItems">
                                    No Work Items Found
                                </q-item-label>

                                <q-item-label class="text-caption text-info" v-if="workItemFilter.isDirty">
                                    Try changing your search criteria
                                </q-item-label>
                                <q-item-label v-else-if="!loading.workItems">
                                    <q-btn 
                                        size="sm" 
                                        icon="mdi-playlist-plus"
                                        :color="$q.dark.isActive ? 'green-13' : 'green'"
                                        @click="workOrderDialogStore.create()"
                                        label="Add work item"
                                    />
                                </q-item-label>
                            </q-inner-loading>

                            <q-inner-loading :showing="loading.workItems">
                                <q-spinner size="1.5rem" />
                                Loading Work Items
                            </q-inner-loading>
                        </q-scroll-area>

                        <q-item v-if="workOrderDialogStore.workItems.filter.total > 0">
                            <q-item-section :side="$q.screen.lt.md">
                                <q-btn
                                    square no-caps
                                    :color="workOrderDialogStore.workItems.filter.from === 1 ? 'grey-8' : 'primary'"
                                    :label="$q.screen.gt.sm ? 'Previous Page' : undefined"
                                    :icon="$q.screen.lt.md ? 'mdi-chevron-left' : undefined"


                                    :disable="workOrderDialogStore.workItems.filter.current_page <= 1"
                                    @click="workItemFilter.page = Math.max(1, workItemFilter.page - 1)"
                                />
                            </q-item-section>
                            <q-item-section class="text-center">
                                <q-item-label>
                                    Showing {{ workOrderDialogStore.workItems.filter.current_page }} of {{ workOrderDialogStore.workItems.filter.last_page }} page{{ workOrderDialogStore.workItems.filter.last_page != 1 ? 's' : '' }}
                                </q-item-label>
                                <q-item-label caption>
                                    Or {{ workOrderDialogStore.workItems.filter.from }}-{{ workOrderDialogStore.workItems.filter.total }} of {{ workOrderDialogStore.workItems.filter.to }} work items
                                </q-item-label>
                                <!-- Popup and helper tooltip should show only when useful -->
                                <template v-if="workOrderDialogStore.workItems.filter.last_page > 1">
                                    <q-tooltip>
                                        <div class="text-bold">
                                            Click to change page manually
                                        </div>
                                        Showing {{ workOrderDialogStore.workItems.filter.current_page }} of {{ workOrderDialogStore.workItems.filter.last_page }} page{{ workOrderDialogStore.workItems.filter.last_page != 1 ? 's' : '' }}
                                    </q-tooltip>

                                    <q-popup-edit 
                                        auto-save 
                                        v-slot="scope"
                                        :model-value="workOrderDialogStore.workItems.filter.form.page"
                                    >
                                        <q-input 
                                            dense
                                            autofocus
                                            type="number"
                                            inputmode="numeric"
                                            :rules="[
                                                val => val > 0 || 'Page must be greater than 0',
                                                val => val <= workOrderDialogStore.workItems.filter.last_page || `Page must be less than ${workOrderDialogStore.workItems.filter.last_page + 1}`
                                            ]"
                                            label="Page"
                                            v-model="scope.value"
                                            @keyup.enter="scope.set" 
                                        />
                                    </q-popup-edit>
                                </template>
                            </q-item-section>
                            <q-item-section :side="$q.screen.lt.md">
                                <q-btn
                                    square no-caps
                                    :color="workOrderDialogStore.workItems.filter.current_page >= workOrderDialogStore.workItems.filter.last_page ? 'grey-8' : 'primary'"
                                    :label="$q.screen.gt.sm ? 'Next Page' : undefined"
                                    :icon="$q.screen.lt.md ? 'mdi-chevron-right' : undefined"
                                    :disable="workOrderDialogStore.workItems.filter.current_page >= workOrderDialogStore.workItems.filter.last_page"
                                    @click="workItemFilter.page = Math.min(workOrderDialogStore.workItems.filter.last_page, workItemFilter.page + 1)"
                                />
                            </q-item-section>
                        </q-item>
                    </div>
                </div>
            </q-card-section>
        </q-card>
    </q-dialog>
</template>

<script>
import { useDialogPluginComponent } from 'quasar';

import WorkItemView from './Components/WorkItem/WorkItemView.vue';
import WorkItemEdit from './Components/WorkItem/WorkItemEdit.vue';

import SwapDialog from './Components/SwapDialog/SwapDialog.vue';

import WorkOrderDetails from './Components/Details.vue';

/** Soon to take over the older WorkOrderDialog solution */
export default {
    name: "WorkOrderDialog",
    emits: [...useDialogPluginComponent.emits],
    components: {
        WorkItemView,
        WorkItemEdit,
        SwapDialog,
        WorkOrderDetails,
    },
    setup () {
        const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent()

        return {
            dialogRef,
            onDialogHide,
            onCancelClick: onDialogCancel,
        }
    },
    data() {
        return {
            workItemColumns: [
                {
                    label: 'Notes',
                    value: "notes"
                },
                {
                    label: 'Work Type',
                    value: "work_type"
                },
                {
                    label: 'Sub-Customer',
                    value: "sub_customer"
                },
                {
                    label: 'Customer',
                    value: "customer"
                },
                {
                    label: 'Worker',
                    value: "lumper"
                },
                {
                    label: 'Created At',
                    value: "created_at"
                },
                {
                    label: 'Updated At',
                    value: "updated_at"
                },
            ],
            rows: [
                {
                    label: '5',
                    value: 5,
                },
                {
                    label: '7',
                    value: 7,
                },
                {
                    label: '10',
                    value: 10,
                },
                {
                    label: '15',
                    value: 15,
                },
                {
                    label: '25',
                    value: 25,
                },
                {
                    label: '50',
                    value: 50,
                },
                {
                    label: 'All',
                    value: 0,
                },
            ],
        };
    },
    computed: {
        workOrderId() {
            return this.workOrderDialogStore.workOrder.id;
        },
        customerId() {
            return this.workOrderDialogStore.workOrder.customer_id;
        },
        subCustomerId() {
            return this.workOrderDialogStore.workOrder.sub_customer_id ?? null;
        },
        customer() {
            return this.workOrderDialogStore.customer;
        },
        identifierTypes() {
            return this.workOrderDialogStore.identifierTypes;
        },
        minimized: {
            get() { return this.workOrderDialogStore.minimized; },
            set(value) { this.workOrderDialogStore.minimized = value; },
        },
        docked: {
            get() { return this.workOrderDialogStore.docked; },
            set(value) {
                if (this.minimized) this.minimized = false;
                this.workOrderDialogStore.docked = value;
            },
        },
        editing: {
            get() { return this.workOrderDialogStore.editing; },
            set(value) { this.workOrderDialogStore.editing = value; },
        },
        workOrder() {
            return this.workOrderDialogStore.workOrder;
        },
        workItems() {
            return this.workOrderDialogStore.workItems.rows;
        },
        workItemsEditing() {
            return this.workOrderDialogStore.workItems.editing;
        },
        newWorkItems() {
            return this.workItemsEditing
                .map((item, index) => ({ item, index }))
                .filter(entry => !entry.item.id);
        },
        workItemFilter() {
            return this.workOrderDialogStore.workItems.filter.form;
        },
        loading() {
            return this.workOrderDialogStore.loading;
        },
    },
    watch: {
        'workOrderDialogStore.open'(to) {
            if (to) return;
            this.workOrderDialogStore.reset();
        },
        'workOrderDialogStore.workItems.filter.form.page'() {
            if (!this.workOrderDialogStore.loaded) return;
            this.workOrderDialogStore.getWorkItems(null, true);
        },
        'workOrderDialogStore.workItems.filter.form.rows'() { this.resetPageAndFetch(); },
        'workOrderDialogStore.workItems.filter.form.search'() { this.resetPageAndFetch(); },
        'workOrderDialogStore.workItems.filter.form.sort'() { this.resetPageAndFetch(); },
        'workOrderDialogStore.workItems.filter.form.direction'() { this.resetPageAndFetch(); },
        'workOrderDialogStore.workItems.filter.form.searchColumn': {
            deep: true,
            handler() { this.resetPageAndFetch(); },
        },
    },
    methods: {
        resetPageAndFetch() {
            if (!this.workOrderDialogStore.loaded) return;
            if (this.workOrderDialogStore.workItems.filter.form.page !== 1) {
                this.workOrderDialogStore.workItems.filter.form.page = 1;
                return;
            }
            this.workOrderDialogStore.getWorkItems(null, true);
        },
        editingIndexFor(id) {
            if (id === null || id === undefined) return -1;
            return this.workItemsEditing.findIndex(v => v.id === id);
        },
        newItemError(editingIndex) {
            return this.workOrderDialogStore.workItems.errors.new.find(e => e.index === editingIndex);
        },
        existingItemError(itemId) {
            return this.workOrderDialogStore.workItems.errors.existing.find(e => e.id === itemId);
        },
    },
}
</script>